import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, MapPin, Users, Trash2, Edit2 } from 'lucide-react';
import { FullPageLoader } from '../components/LoadingSpinner';
import Footer from '../components/Footer';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 12 Pastel colors for trip cards
const PASTEL_COLORS = [
  '#FFB3BA', // Pastel Pink
  '#FFDFBA', // Pastel Peach
  '#FFFFBA', // Pastel Yellow
  '#BAFFC9', // Pastel Mint
  '#BAE1FF', // Pastel Blue
  '#C9C9FF', // Pastel Lavender
  '#FFB3E6', // Pastel Magenta
  '#D4A5A5', // Pastel Rose
  '#FFDAB9', // Pastel Orange
  '#E6E6FA', // Pastel Lilac
  '#B0E0E6', // Pastel Cyan
  '#F0E68C', // Pastel Gold
];

export default function Dashboard() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTripName, setNewTripName] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null); // Trip being edited
  const [editTripName, setEditTripName] = useState('');
  const [editTripColor, setEditTripColor] = useState('');
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  // OPTIMIZATION: Remove 'user' from dependencies to prevent multiple fetches
  useEffect(() => {
    fetchTrips();
  }, []); // Only fetch on mount

  const fetchTrips = async () => {
    try {
      const response = await axios.get(`${API_URL}/trips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrips(response.data);
    } catch (error) {
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  const createTrip = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API_URL}/trips`,
        { name: newTripName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Trip created!');
      setTrips([response.data, ...trips]);
      setNewTripName('');
      setShowDialog(false);
    } catch (error) {
      toast.error('Failed to create trip');
    }
  };

  const deleteTrip = async (tripId) => {
    if (!window.confirm('Are you sure you want to delete this trip?')) return;
    
    try {
      await axios.delete(`${API_URL}/trips/${tripId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trip deleted');
      setTrips(trips.filter(t => t.id !== tripId));
    } catch (error) {
      toast.error('Failed to delete trip');
    }
  };

  const updateTripColor = async (tripId, colorCode) => {
    try {
      await axios.put(
        `${API_URL}/trips/${tripId}/color`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { color_code: colorCode }
        }
      );
      
      setTrips(trips.map(t => t.id === tripId ? {...t, color_code: colorCode} : t));
      toast.success('Trip color updated');
    } catch (error) {
      toast.error('Failed to update trip color');
    }
  };

  const updateTripName = async (tripId, name) => {
    try {
      await axios.put(
        `${API_URL}/trips/${tripId}/name`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { name: name }
        }
      );
      
      setTrips(trips.map(t => t.id === tripId ? {...t, name: name} : t));
      toast.success('Trip name updated');
    } catch (error) {
      toast.error('Failed to update trip name');
    }
  };

  const openEditDialog = (trip) => {
    setEditingTrip(trip);
    setEditTripName(trip.name);
    setEditTripColor(trip.color_code);
  };

  const closeEditDialog = () => {
    setEditingTrip(null);
    setEditTripName('');
    setEditTripColor('');
  };

  const saveTrip = async () => {
    if (!editTripName.trim()) {
      toast.error('Trip name cannot be empty');
      return;
    }

    try {
      await Promise.all([
        updateTripName(editingTrip.id, editTripName.trim()),
        updateTripColor(editingTrip.id, editTripColor)
      ]);
      
      closeEditDialog();
    } catch (error) {
      toast.error('Failed to update trip');
    }
  };

  if (loading) {
    return <FullPageLoader text="Loading trips..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Nomad
          </h1>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/error-logs')}
              data-testid="error-logs-button"
            >
              Error Logs
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/settings')}
              data-testid="settings-button"
            >
              Settings
            </Button>
            <Button
              variant="outline"
              onClick={logout}
              data-testid="logout-button"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Your Trips
            </h2>
            <p className="text-slate-600 mt-1" style={{fontFamily: 'Inter, sans-serif'}}>
              Roadtrip Carpooling: One place to plan every ride.
            </p>
          </div>
          
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-trip-button">
                <Plus className="w-4 h-4 mr-2" />
                New Trip
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Trip</DialogTitle>
              </DialogHeader>
              <form onSubmit={createTrip} className="space-y-4">
                <div>
                  <Label htmlFor="trip-name">Trip Name</Label>
                  <Input
                    id="trip-name"
                    value={newTripName}
                    onChange={(e) => setNewTripName(e.target.value)}
                    placeholder="e.g., Melbourne to Sydney"
                    required
                    data-testid="trip-name-input"
                    className="mt-1"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="create-trip-submit">
                  Create Trip
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Edit Trip Dialog */}
        <Dialog open={editingTrip !== null} onOpenChange={(open) => !open && closeEditDialog()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Trip</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-trip-name">Trip Name</Label>
                <Input
                  id="edit-trip-name"
                  value={editTripName}
                  onChange={(e) => setEditTripName(e.target.value)}
                  className="mt-1"
                  required
                />
              </div>
              
              <div>
                <Label>Trip Color</Label>
                <div className="grid grid-cols-6 gap-3 mt-2">
                  {PASTEL_COLORS.map((color) => (
                    <button
                      key={color}
                      className={`w-10 h-10 rounded-full border-2 shadow-sm hover:scale-110 transition-transform ${
                        editTripColor === color ? 'border-blue-500' : 'border-white'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setEditTripColor(color)}
                      title={color}
                    />
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button onClick={saveTrip} className="flex-1">
                  Save Changes
                </Button>
                <Button variant="outline" onClick={closeEditDialog} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Trips Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Loading trips...</p>
          </div>
        ) : trips.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <MapPin className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600 mb-4">No trips yet. Create your first trip to get started!</p>
              <Button onClick={() => setShowDialog(true)} data-testid="create-first-trip-button">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Trip
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trips.map((trip) => (
              <Card
                key={trip.id}
                className="hover:shadow-lg transition-shadow cursor-pointer relative"
                style={{ 
                  borderLeft: `4px solid ${trip.color_code}`,
                  background: `linear-gradient(to bottom right, ${trip.color_code}15, transparent)` 
                }}
                data-testid={`trip-card-${trip.id}`}
              >
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0" onClick={() => navigate(`/trip/${trip.id}/upload`)}>
                      <CardTitle className="text-xl truncate" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                        {trip.name}
                      </CardTitle>
                      <CardDescription className="mt-2">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 whitespace-nowrap">
                            <Users className="w-4 h-4 flex-shrink-0" />
                            <span>{trip.participant_count || 0} people</span>
                          </span>
                          <span className="text-slate-500 whitespace-nowrap">
                            {trip.driver_count || 0} drivers
                          </span>
                          <span className="text-slate-500 whitespace-nowrap">
                            {trip.passenger_count || 0} passengers
                          </span>
                        </div>
                      </CardDescription>
                    </div>
                    <div className="flex gap-1 flex-shrink-0 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(trip);
                        }}
                        className="h-8 w-8 p-0"
                        data-testid={`edit-trip-${trip.id}`}
                      >
                        <Edit2 className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteTrip(trip.id);
                        }}
                        className="h-8 w-8 p-0"
                        data-testid={`delete-trip-${trip.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button
                    className="w-full"
                    onClick={() => navigate(`/trip/${trip.id}/upload`)}
                    data-testid={`open-trip-${trip.id}`}
                  >
                    Open Trip
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
